import { makeAutoObservable } from "mobx";
import AuthController from "../api/controllers/AuthController";
import UserModel from "../api/models/UserModel";
import AuthService from "../api/services/AuthService";
import {
  AccessProfileStore,
  CompanyStore,
  ContractIntegrationStore,
  MessageStore,
  ProfileStore,
} from "./";

import { AuthException } from "api/exceptions/AuthException";
import { IDashboardFilter } from "api/interfaces";
import FileService from "api/services/FileService";
import UserService from "api/services/UserService";
import { lang } from "../i18nLang";

class Store {
  private completeAuth: boolean = false;
  private userIsAuthenticated: boolean = false;
  private _currentUserWithAllData?: UserModel = undefined;

  public userPhoto?: File = undefined;

  constructor() {
    makeAutoObservable(this);
  }

  get currentUser() {
    return this._currentUserWithAllData;
  }

  get isAthenticated(): boolean {
    return this.userIsAuthenticated;
  }

  reset() {
    this.completeAuth = false;
    this.userIsAuthenticated = false;
    this._currentUserWithAllData = undefined;
  }

  setCurrentUserAccess(access: number) {
    if (this._currentUserWithAllData) {
      this._currentUserWithAllData.access = access;
    }
  }

  async getCurrentUserData() {
    const user = await AuthController.getAuthenticatedUserData().catch(
      (error) => {
        console.error(error);
        MessageStore.error(error.message);
        return undefined;
      }
    );
    this.setUserIsAuthenticated(Boolean(user));
    this.setCurrentUser(user);
  }

  setUserPhoto(file: File) {
    this.userPhoto = file;
  }

  async signOut() {
    this.reset();
    ProfileStore.reset();
    CompanyStore.unsubscribeCompany();
    await AuthService.signOutUser();
  }

  public async signInWithEmail(email: string, password: string) {
    const userAuthenticated = await AuthController.signInWithEmail(
      email,
      password
    );
    await this.getCurrentUserData();
    this.verifyIfUserIsDisabled();
    return userAuthenticated;
  }

  public async createUserAccount(
    name: string,
    email: string,
    password: string
  ) {
    const newUser = await AuthController.createUserAccount(
      name,
      email,
      password
    );
    await this.getCurrentUserData();
    return newUser;
  }

  public async saveContractStatusFiltered(status: string[]) {
    const user = this._currentUserWithAllData;
    if (!user || !status.length) return;

    await UserService.updateContractStatusFiltered(user, status);
  }

  public async saveDashboardFilters(filters: IDashboardFilter) {
    const user = this._currentUserWithAllData;
    if (!user || !filters) return;

    await UserService.updateDashboardFilters(user, filters);
  }

  public verifyCurrentSession(callback: (userAuthenticated: boolean) => void) {
    // Se a sessao ja foi verificada ele apenas chama o callback
    if (this.completeAuth) {
      callback(this.userIsAuthenticated);
      return;
    }

    const onSessionChange = async (user) => {
      try {
        await this.getCurrentUserData();
        this.verifyIfUserIsDisabled();
        // Observa alterações na empresa que tem os dados da integração
        CompanyStore.subscribeCompany((company) => {
          // Obtém o perfil de acesso do usuário logado para validar as permissões de acesso.
          AccessProfileStore.getAndSetCurrentUserAccessProfile();
          // Verifica a autenticação da integração com os dados alterados
          ContractIntegrationStore.authenticateUser(company.integrationSetup)
            .then((authenticated) => {
              // Se a API foi conectado com sucesso, atualiza as listas do ERP
              if (authenticated) ContractIntegrationStore.fetchMainListsOfERP();
            })
            .catch((error) => {});
        });
        this.completeAuth = true;

        if (callback) {
          callback(Boolean(user));
        }
      } catch (error) {
        this.signOut();
        if (callback) {
          callback(false);
        }
      }
    };

    const onSignNewSession = async () => {
      await this.signOut();
      MessageStore.info(lang("login.newSession"), true);
    };

    const onSignError = async (error) => {
      await this.signOut();
      MessageStore.error(error.message || "Error on Session", true);
    };

    // Add o listener para verificar a sessao
    AuthService.subscribeAuthObserver(
      onSessionChange,
      onSignNewSession,
      onSignError
    );
  }

  private setUserIsAuthenticated(value?: boolean) {
    const authenticated =
      value === undefined ? AuthService.isAuthenticated : value;
    this.userIsAuthenticated = authenticated;
    if (!authenticated) this.setCurrentUser(undefined);
  }

  private setCurrentUser(user?: UserModel) {
    this._currentUserWithAllData = user;
    this.userPhoto = undefined;

    if (user?.photoURL) {
      const photo = user.photoURL;
      FileService.downloadFrom(photo).then((fileDownloaded) => {
        this.userPhoto = fileDownloaded;
      });
    }
  }

  private verifyIfUserIsDisabled() {
    const user = this._currentUserWithAllData;
    if (user && user.disabled) {
      throw new AuthException(AuthException.codes.USER_DISABLED);
    }
  }
}

const UserStore = new Store();
export default UserStore;
